import * as React from 'react'
import { Button, Heading } from '@radix-ui/themes'
import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { FormDefaults } from '~/components/common/form-defaults.tsx'
import { RadioGroup } from '~/components/ui/radio-group.tsx'
import { camelToSnake } from '~/utils/camel-to-snake.ts'
import { TextField } from '~/components/ui/text-field.tsx'
import { useHydrated } from 'remix-utils/use-hydrated'
import cn from 'clsx'
import { useTranslation } from 'react-i18next'
import { INDEX_I18NEXT } from '~/routes/($lang)._index/index.component.tsx'
import { useFetcher, useOutletContext, useSearchParams } from '@remix-run/react'
import { type App, Context, ROOT_INPUTS } from '~/routes/_layout/root.component.tsx'
import { parseWithZod } from '@conform-to/zod'
import { loginSchema } from '~/routes/($lang)._auth.login/login.component.tsx'
import { OFFER_FORM, OFFER_INPUTS, OFFER_SEARCH_PARAMS } from '~/routes/($lang)._index/offer.tsx'
import { Locale } from '~/models/locale.ts'
import { useNotify } from '~/utils/hooks/use-notify.ts'
import * as sea from 'node:sea'

const formData = [
  {
    title: 'roofType',
    inputs: [
      {
        type: RadioGroup,
        name: OFFER_INPUTS.roofType,
        values: [
          'slopingRoof',
          'flatRoof',
        ],
      },
    ]
  }, {
    title: 'youWant',
    inputs: [
      {
        type: RadioGroup,
        name: OFFER_INPUTS.youWant,
        values: [
          'deliveryAndInstallation',
          'deliveryOnly',
        ],
      },
    ]
  }, {
    title: 'squareFootOfRoof',
    inputs: [
      {
        type: RadioGroup,
        name: OFFER_INPUTS.squareFootOfRoof,
        values: [
          'upTo50',
          '50To100',
          '100To200',
          'over200',
          'unsafe',
        ],
      },
    ]
  }, {
    title: 'needWallbox',
    inputs: [
      {
        type: RadioGroup,
        name: OFFER_INPUTS.needWallbox,
        values: [
          'yes',
          'no',
        ],
      },
    ]
  }, {
    title: 'needEnergyStorageSystem',
    inputs: [
      {
        type: RadioGroup,
        name: OFFER_INPUTS.needEnergyStorageSystem,
        values: [
          'yes',
          'no',
        ],
      },
    ]
  }, {
    title: 'privateData',
    inputs: [
      {
        type: TextField,
        name: OFFER_INPUTS.name,
        inputType: 'text',
      }, {
        type: TextField,
        name: OFFER_INPUTS.email,
        inputType: 'email',
      },  {
        type: TextField,
        name: OFFER_INPUTS.phone,
        inputType: 'tel',
      }, {
        type: TextField,
        name: OFFER_INPUTS.postcode,
        inputType: 'tel',
      }, {
        type: TextField,
        name: OFFER_INPUTS.street,
        inputType: 'text',
      },
    ]
  },
]

type OfferFormProps = {
  locale: Locale
  setApp: React.Dispatch<React.SetStateAction<App>>
}

const OfferForm: React.FC< OfferFormProps> = ({ locale, setApp }) => {
  const { t } = useTranslation(INDEX_I18NEXT.routeIndex)
  const [searchParams, setSearchParams] = useSearchParams()
  const fetcher = useFetcher({ key: OFFER_FORM.fetcherKey })
  const formValues = {
    [ROOT_INPUTS.subaction]: OFFER_FORM.subaction,
    [ROOT_INPUTS.noJs]: 'false',
  }
  const [form, fields] = useForm({
    lastResult: fetcher.data || undefined,
    defaultValue: {
     ...formValues,
    },
  })

  React.useEffect(() => {
    if (fetcher.formData) {
      searchParams.set(OFFER_SEARCH_PARAMS.offerRequested, 'on')
      setSearchParams(searchParams)
      setApp((prev) => ({
        ...prev,
        displayModal: false,
      }))
    }
  }, [fetcher.state])

  return (
    <>
      <Heading color="green" className="text-center !mb-7">
        {t('common.offer.dialog.title')}
      </Heading>
      <FormProvider context={form.context}>
        <fetcher.Form
          method="post"
          action={`/${locale}${OFFER_FORM.action}`}
          {...getFormProps(form)}
          preventScrollReset
        >
          <FormDefaults
            form={form}
            fields={fields}
            values={formValues}
          />
          <div className="flex flex-col gap-y-6">
            {formData.map(({ title, inputs }) => (
              <div
                key={title}
                className="flex flex-col gap-y-2"
              >
                <Heading color="gray" weight="medium" size="4">
                  {t(`common.offer.dialog.${title}`)}
                </Heading>
                <div className="flex flex-col gap-y-1">
                  {/* eslint-disable-next-line array-callback-return */}
                  {inputs.map(({ type, name, values = undefined, text = undefined, inputType = undefined }, i) => {
                    switch (type) {
                      case RadioGroup:
                        return (
                          <div key={name}>
                            <RadioGroup.Root name={name} t={t} meta={fields[name]} className="gap-y-2">
                              {values.map((value: string) => (
                                <RadioGroup.Item key={value} value={camelToSnake(value)}>
                                  {t(`common.offer.dialog.${value}`)}
                                </RadioGroup.Item>
                              ))}
                            </RadioGroup.Root>
                          </div>
                        )
                      case TextField:
                        return (
                          <div key={name} className={cn({
                            'mt-4': !i,
                            'mt-7': !!i,
                          })}>
                            <TextField
                              name={name}
                              type={inputType}
                              t={t}
                              meta={fields[name]}
                            >
                              {t(`common.offer.dialog.${name}`)}
                            </TextField>
                          </div>
                        )
                    }
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-12">
            <Button type="submit" variant="soft" size="3">
              {t('common.offer.dialog.buttonSubmit')}
            </Button>
          </div>
        </fetcher.Form>
      </FormProvider>
    </>
  )
}

export {
  OfferForm,
  OfferFormProps,
}
